import { ThemeEvents } from '@theme/events';

/**
 * Storefront UX analytics — the SINGLE owner of Shopify.analytics.publish
 * calls in this theme.
 *
 * Architecture (see .claude/skills/shopify-tracking/SKILL.md for the full
 * conventions + event taxonomy):
 *
 *  1. `window.publishStorefrontEvent(name, payload, options)` — the one
 *     publish entry point. Feature JS calls it optionally-chained
 *     (`window.publishStorefrontEvent?.(...)`) so nothing breaks if this
 *     module fails to load. Enriches every payload with page_path,
 *     page_type and timestamp, burst-dedupes identical events, and queues
 *     events fired before Shopify.analytics is ready.
 *
 *  2. Declarative layers (no JS needed in features — just Liquid attributes):
 *     - data-analytics-click="event_name"     → publish on click
 *     - data-analytics-view="event_name"      → publish once when ≥35% visible
 *     - data-analytics-expand="object"        → click on an [aria-expanded]
 *                                               trigger → object_expanded /
 *                                               object_collapsed
 *     - data-analytics-toggle="object"        → native <details> toggle →
 *                                               object_expanded / _collapsed
 *     - data-analytics-dialog="object"        → on a DialogComponent →
 *                                               object_opened / object_closed
 *                                               (_closed carries seconds_open)
 *     - data-analytics-payload='{"k":"v"}'    → JSON merged into the payload
 *     - data-analytics-context='{"k":"v"}'    → JSON inherited by every event
 *                                               fired from descendants
 *       (placement, recommendation_type, etc. — nearest ancestor wins)
 *
 *  3. Theme event-bus bridges — cart:update (add sources), cart:error,
 *     variant:update, quantity-selector:update, filter:update (diffed into
 *     filter_applied / filter_removed / filter_cleared / sort_changed),
 *     slideshow:select (product gallery), media:started-playing.
 *
 *  All listeners are document-level + capture-phase, so they survive section
 *  morphs, AJAX cart updates, quick-add rendering and drawer refreshes.
 *  Never call Shopify.analytics.publish directly from feature code.
 */

const DEBUG_KEY = 'sfa:debug';
const BURST_WINDOW_MS = 300;

/** @type {Map<string, number>} recent identical events (name+payload → ts) */
const recentEvents = new Map();
/** @type {Set<string>} once-per-page keys (impressions) */
const firedOnce = new Set();
/** @type {Array<[string, Object]>} events fired before Shopify.analytics was ready */
let pendingQueue = [];
let queueFlushTimer = 0;

function debugEnabled() {
  try {
    return window.localStorage?.getItem(DEBUG_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * The theme template name ('product', 'collection', ...). scripts.liquid
 * declares the global as `const Theme = {...}` — a top-level lexical binding
 * that is visible to modules by bare name but is NOT a window property, so
 * `window.Theme` is always undefined. The typeof guard keeps this safe if
 * the global ever goes missing.
 * @returns {string | null}
 */
function pageType() {
  try {
    return (typeof Theme !== 'undefined' && Theme?.template?.name) || null;
  } catch {
    return null;
  }
}

/**
 * Publishes a storefront UX event through Shopify's custom event pipeline.
 *
 * @param {string} name - snake_case object_action event name (owned by the
 *   codebase — never merchant-editable, never containing ids/dynamic values).
 * @param {Object} [payload] - Contextual properties. No PII.
 * @param {{ once?: string }} [options] - `once`: a key that limits the event
 *   to once per page view (used for impressions and threshold events).
 * @returns {boolean} whether the event was published (or queued).
 */
function publishStorefrontEvent(name, payload = {}, options = {}) {
  if (!name || typeof name !== 'string') return false;

  const enriched = {
    ...payload,
    page_path: window.location.pathname,
    page_type: pageType(),
    timestamp: new Date().toISOString(),
  };

  // Once-per-page guard (impressions, thresholds).
  if (options.once) {
    if (firedOnce.has(options.once)) return false;
    firedOnce.add(options.once);
  }

  // Burst dedupe: identical name+payload within BURST_WINDOW_MS is a double
  // fire (two instances of a component reacting to one cart update, a click
  // handler running in both capture and bubble, ...), not a real interaction.
  const burstKey = `${name}|${stableJson(payload)}`;
  const now = Date.now();
  const last = recentEvents.get(burstKey);
  if (last && now - last < BURST_WINDOW_MS) return false;
  recentEvents.set(burstKey, now);
  if (recentEvents.size > 100) {
    for (const [key, ts] of recentEvents) {
      if (now - ts > BURST_WINDOW_MS) recentEvents.delete(key);
    }
  }

  if (debugEnabled()) {
    console.debug(`[storefront-analytics] ${name}`, enriched);
  }

  notifySubscribers(name, enriched);

  // Forward to the top-level GA4 tag installed in theme.liquid, so every
  // storefront custom event reaches GA4 with the same client_id/session as
  // pageviews. This is intentionally the SINGLE forwarding site: do NOT attach
  // gtag() to individual component handlers. It runs BEFORE the Shopify publish
  // (and independently of Shopify.analytics readiness) so early/queued events
  // reach GA4 too, and sits after the burst-dedupe/once guards above so GA4
  // never receives duplicates. The Customer Events custom pixel runs in a
  // sandboxed frame with its own gtag — forwarding here keeps sessions unified;
  // if that pixel also forwards these events, remove it to avoid double-counting.
  if (typeof window.gtag === 'function') {
    if (debugEnabled()) console.debug('[GA4 CUSTOM EVENT]', name, enriched);
    window.gtag('event', name, {
      ...enriched,
      send_to: 'G-JM5SFYWH4M',
    });
  }

  const publish = window.Shopify?.analytics?.publish;
  if (typeof publish === 'function') {
    try {
      window.Shopify.analytics.publish(name, enriched);
    } catch (error) {
      if (debugEnabled()) console.debug('[storefront-analytics] publish failed', error);
      return false;
    }
    return true;
  }

  // Shopify.analytics isn't ready yet (very early events) — queue and retry.
  if (pendingQueue.length < 30) pendingQueue.push([name, enriched]);
  if (!queueFlushTimer) {
    let attempts = 0;
    queueFlushTimer = window.setInterval(() => {
      attempts += 1;
      const publishFn = window.Shopify?.analytics?.publish;
      if (typeof publishFn === 'function') {
        const queued = pendingQueue;
        pendingQueue = [];
        window.clearInterval(queueFlushTimer);
        queueFlushTimer = 0;
        for (const [queuedName, queuedPayload] of queued) {
          try {
            window.Shopify.analytics.publish(queuedName, queuedPayload);
          } catch {
            // drop
          }
        }
      } else if (attempts > 20) {
        window.clearInterval(queueFlushTimer);
        queueFlushTimer = 0;
        pendingQueue = [];
      }
    }, 500);
  }
  return true;
}

/** Deterministic JSON for dedupe keys (key order independent, shallow). */
function stableJson(obj) {
  if (!obj || typeof obj !== 'object') return String(obj);
  const keys = Object.keys(obj).sort();
  return JSON.stringify(obj, keys);
}

/**
 * In-page observers of the published event stream (friction.js builds its
 * repetition/reopen/dead-click signals from this). Fired for every event that
 * passes the once/burst guards, whether delivered or queued. Callbacks must
 * never throw into the publish path.
 * @type {Array<(name: string, payload: Object) => void>}
 */
const publishSubscribers = [];

/** @param {(name: string, payload: Object) => void} callback */
export function onStorefrontEvent(callback) {
  if (typeof callback === 'function') publishSubscribers.push(callback);
}

function notifySubscribers(name, payload) {
  for (const callback of publishSubscribers) {
    try {
      callback(name, payload);
    } catch {
      // observers never break publishing
    }
  }
}

window.publishStorefrontEvent = publishStorefrontEvent;
export { publishStorefrontEvent, contextFor, placementFor, stableJson };

/* ------------------------------------------------------------------ */
/* Context + payload resolution                                        */
/* ------------------------------------------------------------------ */

/**
 * Merges every ancestor's data-analytics-context JSON, outermost first, so
 * the nearest ancestor wins. Gives events their placement / component /
 * recommendation_type without each trigger repeating it.
 * @param {Element | null} element
 * @returns {Object}
 */
function contextFor(element) {
  const contexts = [];
  let node = element;
  while (node && node !== document.documentElement) {
    const raw = node.getAttribute?.('data-analytics-context');
    if (raw) {
      try {
        contexts.unshift(JSON.parse(raw));
      } catch {
        // malformed context attribute — skip it
      }
    }
    node = node.parentElement;
  }
  return Object.assign({}, ...contexts);
}

/**
 * Parses an element's own data-analytics-payload JSON.
 * @param {Element} element
 * @returns {Object}
 */
function payloadFor(element) {
  const raw = element.getAttribute('data-analytics-payload');
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

/**
 * Context + payload for a trigger element, with an optional base.
 * @param {Element} element
 * @param {Object} [base]
 */
function resolvePayload(element, base = {}) {
  return { ...contextFor(element), ...base, ...payloadFor(element) };
}

/**
 * Best-effort placement when no data-analytics-context ancestor provides one.
 * @param {Element | null} element
 * @returns {string}
 */
function fallbackPlacement(element) {
  if (!element || !(element instanceof Element)) {
    return pageType() === 'product' ? 'product_page' : 'page';
  }
  if (element.closest('#quick-add-dialog, quick-add-dialog')) return 'quick_add';
  if (element.closest('cart-quick-add-component')) return 'cart_quick_add';
  if (element.closest('sticky-add-to-cart')) return 'sticky_bar';
  if (element.closest('cart-drawer-component')) return 'cart_drawer';
  if (element.closest('cart-items-component')) return 'cart_page';
  if (element.closest('.product-information, [data-testid="product-information"]')) return 'product_page';
  if (element.closest('product-card, .product-card')) return 'product_card';
  return pageType() === 'product' ? 'product_page' : 'page';
}

/** @param {Element | null} element */
function placementFor(element) {
  const context = contextFor(element);
  return context.placement || fallbackPlacement(element);
}

/** Extracts a product handle from a /products/<handle> URL, if present. */
function productHandleFromUrl(url) {
  const match = /\/products\/([a-z0-9-]+)/i.exec(url || '');
  return match ? match[1] : null;
}

/* ------------------------------------------------------------------ */
/* 1. Declarative click tracking                                       */
/* ------------------------------------------------------------------ */

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // data-analytics-click="event_name"
    const clickEl = target.closest('[data-analytics-click]');
    if (clickEl) {
      const name = clickEl.getAttribute('data-analytics-click');
      if (name) {
        const base = {};
        const link = clickEl.closest('a[href]') || clickEl.querySelector?.('a[href]');
        if (link instanceof HTMLAnchorElement && link.getAttribute('href')) {
          base.destination_url = link.getAttribute('href');
        }
        publishStorefrontEvent(name, resolvePayload(clickEl, base));
      }
    }

    // data-analytics-expand="object" on an [aria-expanded] trigger.
    // Capture phase runs BEFORE the component flips the attribute, so
    // aria-expanded="false" here means the click is expanding.
    const expandEl = target.closest('[data-analytics-expand]');
    if (expandEl) {
      const object = expandEl.getAttribute('data-analytics-expand');
      const expanded = expandEl.getAttribute('aria-expanded') === 'true';
      if (object) {
        publishStorefrontEvent(`${object}_${expanded ? 'collapsed' : 'expanded'}`, resolvePayload(expandEl));
      }
    }

    trackProductCardClick(event, target);
    trackNavClick(target);
    trackSearchResultClick(target);
  },
  { capture: true }
);

/* ------------------------------------------------------------------ */
/* 2. Impressions (IntersectionObserver)                               */
/* ------------------------------------------------------------------ */

/** @type {WeakSet<Element>} elements already handed to the observer */
const observedElements = new WeakSet();

const impressionObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const element = entry.target;
            impressionObserver?.unobserve(element);
            const name = element.getAttribute('data-analytics-view');
            if (!name) continue;
            const payload = resolvePayload(element);
            // Impressions fire once per page per distinct event+payload, so a
            // section morph re-rendering the same element doesn't refire it.
            publishStorefrontEvent(name, payload, { once: `view:${name}|${stableJson(payload)}` });
          }
        },
        { threshold: 0.35 }
      )
    : null;

function observeImpressions(root) {
  if (!impressionObserver || !(root instanceof Element || root instanceof Document)) return;
  const candidates = [];
  if (root instanceof Element && root.hasAttribute('data-analytics-view')) candidates.push(root);
  root.querySelectorAll?.('[data-analytics-view]').forEach((el) => candidates.push(el));
  for (const el of candidates) {
    if (observedElements.has(el)) continue;
    observedElements.add(el);
    impressionObserver.observe(el);
  }
}

if (impressionObserver) {
  const scan = () => observeImpressions(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  // Re-scan after morphs / AJAX renders, debounced.
  let scanTimer = 0;
  new MutationObserver(() => {
    if (scanTimer) return;
    scanTimer = window.setTimeout(() => {
      scanTimer = 0;
      observeImpressions(document);
    }, 250);
  }).observe(document.documentElement, { childList: true, subtree: true });
}

/* ------------------------------------------------------------------ */
/* 3. Dialog bridge (modals & drawers)                                 */
/* ------------------------------------------------------------------ */

/** @type {WeakMap<Element, number>} open timestamps for seconds_open */
const dialogOpenedAt = new WeakMap();

document.addEventListener(
  'dialog:open',
  (event) => {
    const component = event.target instanceof Element ? event.target : null;
    const named = component?.closest('[data-analytics-dialog]');
    if (!named) return;
    const object = named.getAttribute('data-analytics-dialog');
    if (!object) return;
    dialogOpenedAt.set(named, Date.now());
    publishStorefrontEvent(`${object}_opened`, resolvePayload(named));
  },
  { capture: true }
);

document.addEventListener(
  'dialog:close',
  (event) => {
    const component = event.target instanceof Element ? event.target : null;
    const named = component?.closest('[data-analytics-dialog]');
    if (!named) return;
    const object = named.getAttribute('data-analytics-dialog');
    if (!object) return;
    const openedAt = dialogOpenedAt.get(named);
    const base = {};
    if (openedAt) {
      base.seconds_open = Math.round((Date.now() - openedAt) / 1000);
      dialogOpenedAt.delete(named);
    }
    publishStorefrontEvent(`${object}_closed`, resolvePayload(named, base));
  },
  { capture: true }
);

/* ------------------------------------------------------------------ */
/* 4. <details> toggle bridge (accordions)                             */
/* ------------------------------------------------------------------ */

// Details opened by the parser / open-by-default JS also fire 'toggle'; only
// toggles shortly after a real gesture are shopper interactions.
let lastGestureAt = 0;
const recordGesture = () => {
  lastGestureAt = Date.now();
};
document.addEventListener('pointerdown', recordGesture, { capture: true, passive: true });
document.addEventListener('keydown', recordGesture, { capture: true, passive: true });

document.addEventListener(
  'toggle',
  (event) => {
    if (Date.now() - lastGestureAt > 1500) return;
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    const named = details.closest('[data-analytics-toggle]');
    if (!named) return;
    const object = named.getAttribute('data-analytics-toggle');
    if (!object) return;
    const label = details.querySelector('summary')?.textContent?.trim().slice(0, 80) || null;
    publishStorefrontEvent(`${object}_${details.open ? 'expanded' : 'collapsed'}`, resolvePayload(named, { label }));
  },
  { capture: true }
);

/* ------------------------------------------------------------------ */
/* 5. Theme event-bus bridges                                          */
/* ------------------------------------------------------------------ */

// Cart adds — every add path dispatches cart:update with a `source` string.
// Shopify's native product_added_to_cart pixel event stays authoritative for
// the add itself; cart_item_added adds the UX context (placement/source).
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = event.detail;
  const data = detail?.data || {};
  if (data.didError) return;
  const target = event.target instanceof Element ? event.target : null;
  const placement = placementFor(target);

  switch (data.source) {
    case 'product-form-component': {
      const variantId = /^\d+$/.test(String(detail.sourceId || '')) ? String(detail.sourceId) : null;
      publishStorefrontEvent('cart_item_added', {
        ...contextFor(target),
        product_id: data.productId || null,
        variant_id: variantId,
        quantity: data.itemCount || 1,
        placement,
        component: 'product_form',
      });
      break;
    }
    case 'cart-line-upsell':
      // Default offer_type before the context spread, so a surface can
      // override it via data-analytics-context (e.g. a bundle card sets
      // offer_type: cart_recommendation_bundle).
      publishStorefrontEvent('cart_upsell_accepted', {
        offer_type: 'cart_recommendation',
        ...contextFor(target),
        product_id: data.productId || null,
        placement,
      });
      break;
    case 'cart-quick-add':
      // Multi-variant upsell cards open the Cart Quick Add chooser; the
      // resolved add is still accepting a cart recommendation.
      publishStorefrontEvent('cart_upsell_accepted', {
        offer_type: 'cart_recommendation',
        ...contextFor(target),
        product_id: data.productId || null,
        variant_id: data.variantId ? String(data.variantId) : null,
        quantity: data.itemCount || 1,
        placement,
        component: 'cart_quick_add',
      });
      break;
    case 'cart-bundle-upsell':
      publishStorefrontEvent('bundle_offer_accepted', {
        offer_type: 'cart_bundle_swap',
        ...contextFor(target),
        placement,
      });
      break;
    default:
      // 'cart-items-component' (quantity/remove) is tracked through the
      // quantity-selector bridge + remove-button click attributes instead,
      // so a single change never double-fires.
      break;
  }
});

document.addEventListener(ThemeEvents.cartError, (event) => {
  const data = event.detail?.data || {};
  publishStorefrontEvent('cart_error', {
    message: typeof data.message === 'string' ? data.message.slice(0, 140) : null,
    source: event.detail?.sourceId || null,
  });
});

// Variant / format selection on any product surface (PDP, quick add, cart quick add).
document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const detail = event.detail;
  const variant = detail?.resource;
  const target = event.target instanceof Element ? event.target : null;
  publishStorefrontEvent('variant_selected', {
    ...contextFor(target),
    product_id: detail?.data?.productId || null,
    variant_id: variant?.id ? String(variant.id) : null,
    available: typeof variant?.available === 'boolean' ? variant.available : null,
    placement: placementFor(target),
  });
});

// Quantity changes — cart lines vs product forms. Debounced per line so a
// 1→5 click burst publishes one event with the final quantity.
/** @type {Map<string, number>} */
const quantityTimers = new Map();
document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const detail = event.detail;
  const target = event.target instanceof Element ? event.target : null;
  const inCart = target?.closest('cart-items-component');
  const key = inCart ? `cart:${detail?.cartLine ?? 'na'}` : `form:${placementFor(target)}`;

  const existing = quantityTimers.get(key);
  if (existing) window.clearTimeout(existing);
  quantityTimers.set(
    key,
    window.setTimeout(() => {
      quantityTimers.delete(key);
      const quantity = Number(detail?.quantity);
      if (Number.isNaN(quantity)) return;
      if (inCart) {
        // Row lookup gives us the variant behind the changed line.
        const row = inCart.querySelector(`[data-line="${detail.cartLine}"]`);
        const variantId = row?.getAttribute('data-key')?.split(':')[0] || null;
        if (quantity === 0) {
          publishStorefrontEvent('cart_item_removed', {
            variant_id: variantId,
            method: 'quantity_zero',
            placement: placementFor(inCart),
          });
        } else {
          publishStorefrontEvent('cart_quantity_changed', {
            variant_id: variantId,
            quantity,
            placement: placementFor(inCart),
          });
        }
      } else {
        publishStorefrontEvent('quantity_changed', {
          quantity,
          placement: placementFor(target),
        });
      }
    }, 600)
  );
});

// Filter / sort — diff the query params against the previous state.
let previousFilterParams = new URLSearchParams(window.location.search);

/** Human-stable filter name from a filter.* param. */
function filterNameFromParam(param) {
  return (
    param
      .replace(/^filter\./, '')
      .replace(/^(p\.m\.|v\.option\.|p\.|v\.)/, '')
      // price range params end in .gte / .lte — one logical "price" filter
      .replace(/\.(gte|lte)$/, '')
  );
}

/** @param {URLSearchParams} params */
function filterEntries(params) {
  const entries = [];
  for (const [key, value] of params.entries()) {
    if (key.startsWith('filter.')) entries.push(`${key}=${value}`);
  }
  return entries;
}

document.addEventListener(ThemeEvents.FilterUpdate, (event) => {
  const newParams = event.detail?.queryParams;
  if (!(newParams instanceof URLSearchParams)) return;
  const oldParams = previousFilterParams;
  previousFilterParams = new URLSearchParams(newParams);

  const oldSort = oldParams.get('sort_by');
  const newSort = newParams.get('sort_by');
  if (newSort !== oldSort && (newSort || oldSort)) {
    publishStorefrontEvent('sort_changed', { sort_value: newSort || 'default' });
  }

  const oldEntries = filterEntries(oldParams);
  const newEntries = filterEntries(newParams);
  const oldSet = new Set(oldEntries);
  const newSet = new Set(newEntries);

  const added = newEntries.filter((entry) => !oldSet.has(entry));
  const removed = oldEntries.filter((entry) => !newSet.has(entry));

  if (removed.length > 1 && added.length === 0 && newEntries.length === 0) {
    publishStorefrontEvent('filter_cleared', { count: removed.length });
    return;
  }

  for (const entry of added) {
    const [key, ...rest] = entry.split('=');
    publishStorefrontEvent('filter_applied', {
      filter_name: filterNameFromParam(key),
      filter_value: rest.join('=').slice(0, 100),
    });
  }
  for (const entry of removed) {
    const [key, ...rest] = entry.split('=');
    publishStorefrontEvent('filter_removed', {
      filter_name: filterNameFromParam(key),
      filter_value: rest.join('=').slice(0, 100),
    });
  }
});

// Product gallery navigation (arrows / thumbnails / swipe) — the gallery's
// slideshow emits slideshow:select with userInitiated + trigger.
document.addEventListener('slideshow:select', (event) => {
  const detail = event.detail;
  if (!detail?.userInitiated) return;
  const target = event.target instanceof Element ? event.target : null;
  if (!target?.closest('media-gallery, product-gallery')) return;
  publishStorefrontEvent('gallery_media_changed', {
    index: detail.index,
    trigger: detail.trigger || null,
    placement: placementFor(target),
  });
});

// Product media playback (video / 3D model).
document.addEventListener(ThemeEvents.mediaStartedPlaying, (event) => {
  const resource = event.detail?.resource;
  const element = resource instanceof Element ? resource : null;
  const mediaType = element?.querySelector?.('model-viewer') ? 'model' : 'video';
  publishStorefrontEvent('media_started', {
    media_type: mediaType,
    placement: placementFor(element),
  });
});

/* ------------------------------------------------------------------ */
/* 6. Search                                                           */
/* ------------------------------------------------------------------ */

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/search')) return;
    const query = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="q"]'))?.value?.trim();
    if (!query) return;
    publishStorefrontEvent('search_submitted', {
      search_term: query.slice(0, 100),
      placement: form.closest('predictive-search-component') ? 'search_modal' : 'search_page',
    });
  },
  { capture: true }
);

/** @param {Element} target */
function trackSearchResultClick(target) {
  const component = target.closest('predictive-search-component');
  if (!component) return;
  const link = target.closest('a[href]');
  if (!(link instanceof HTMLAnchorElement)) return;

  let resultType = 'other';
  if (link.closest('.predictive-search-results__wrapper-products')) resultType = 'product';
  else if (link.closest('.predictive-search-results__wrapper-queries')) resultType = 'suggestion';
  else if (link.closest('.predictive-search-results__authors-list')) resultType = 'author';

  const query = /** @type {HTMLInputElement | null} */ (
    component.querySelector('input[type="search"], input[name="q"]')
  )?.value?.trim();

  publishStorefrontEvent('search_result_clicked', {
    search_term: query ? query.slice(0, 100) : null,
    destination_url: link.getAttribute('href'),
    result_type: resultType,
    placement: 'search_modal',
  });
}

/* ------------------------------------------------------------------ */
/* 7. Navigation + product cards                                       */
/* ------------------------------------------------------------------ */

/** @param {Element} target */
function trackNavClick(target) {
  const link = target.closest('a[href]');
  if (!(link instanceof HTMLAnchorElement)) return;

  // Breadcrumb markup is echo-built inside a {% liquid %} tag, so it carries
  // a class contract instead of a data attribute.
  if (link.classList.contains('breadcrumb-block__link')) {
    publishStorefrontEvent('breadcrumb_clicked', {
      destination_url: link.getAttribute('href'),
      placement: placementFor(link),
    });
    return;
  }

  if (link.classList.contains('menu-list__link') || link.closest('.menu-list__submenu')) {
    publishStorefrontEvent('nav_clicked', {
      destination_url: link.getAttribute('href'),
      nav_level: link.closest('.menu-list__submenu') ? 'submenu' : 'top',
      placement: 'header',
    });
    return;
  }

  if (link.closest('header-drawer')) {
    publishStorefrontEvent('nav_clicked', {
      destination_url: link.getAttribute('href'),
      nav_level: 'drawer',
      placement: 'header',
    });
  }
}

/**
 * Product card clicks anywhere (collection grid, search, recommendations,
 * carousels). recommendation_clicked when an ancestor context declares a
 * recommendation_type, product_card_clicked otherwise.
 * @param {MouseEvent} event
 * @param {Element} target
 */
function trackProductCardClick(event, target) {
  const card = target.closest('product-card, .product-card');
  if (!card) return;
  const link = target.closest('a[href]');
  if (!(link instanceof HTMLAnchorElement)) return;
  const href = link.getAttribute('href') || '';
  const handle = productHandleFromUrl(href);
  if (!handle) return;

  const context = contextFor(card);
  const payload = {
    ...context,
    product_handle: handle,
    destination_url: href,
    placement: context.placement || fallbackPlacement(card),
  };
  publishStorefrontEvent(context.recommendation_type ? 'recommendation_clicked' : 'product_card_clicked', payload);
}

/* ------------------------------------------------------------------ */
/* 8. Scroll depth                                                     */
/* ------------------------------------------------------------------ */

// page_scrolled at 25/50/75/90% of the document, once each per page view.
// Passive + rAF-throttled; short pages (no meaningful scroll range) publish
// nothing rather than instant-firing every threshold.
const SCROLL_DEPTH_MARKS = [25, 50, 75, 90];
const scrollMarksFired = new Set();
let scrollFrame = 0;

function checkScrollDepth() {
  scrollFrame = 0;
  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - window.innerHeight;
  if (scrollable < window.innerHeight * 0.5) return; // too short to be a signal
  const depth = ((window.scrollY + window.innerHeight) / doc.scrollHeight) * 100;
  for (const mark of SCROLL_DEPTH_MARKS) {
    if (depth >= mark && !scrollMarksFired.has(mark)) {
      scrollMarksFired.add(mark);
      publishStorefrontEvent('page_scrolled', { depth_pct: mark }, { once: `page_scrolled:${mark}` });
    }
  }
}

window.addEventListener(
  'scroll',
  () => {
    if (!scrollFrame) scrollFrame = window.requestAnimationFrame(checkScrollDepth);
  },
  { passive: true }
);
